import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Edit2, Trash2, Pause, Play, ExternalLink, Building2, Activity, UserCog,
  MessageCircle, Wifi, WifiOff, RefreshCw, Unlink2, QrCode, ShieldCheck, Inbox, Mail, Phone
} from 'lucide-react';
import { useSuperAdmin } from '../contexts/AuthContext';
import api from '../utils/api';
import AppSidebar from '../components/AppSidebar';
import StatusBadge from '../components/StatusBadge';
import TenantModal from '../components/TenantModal';
import TenantAdminModal from '../components/TenantAdminModal';
import ProfileModal from '../components/ProfileModal';

const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return iso; }
};

const GastifyIcon = () => (
  <img src="/gastify-icon-192.png" alt="Gastify" className="w-10 h-10 rounded-2xl object-cover flex-shrink-0 shadow-md border border-teal-500/30"
    data-testid="sa-topbar-logo" />
);

const TITLES = {
  clientes: { title: 'Clientes', sub: 'Cada cliente possui seu próprio banco de dados isolado' },
  whatsapp: { title: 'WhatsApp Bot', sub: 'Conexão do número que atende os clientes' },
  solicitacoes: { title: 'Solicitações', sub: 'Pedidos de acesso vindos da página inicial' },
  auditoria: { title: 'Log de Auditoria', sub: 'Tudo o que os outros superadmins fizeram — visível só pra você' },
};

export default function SuperAdminDashboard() {
  const { admin, logout, refreshAdmin } = useSuperAdmin();
  const navigate = useNavigate();
  const [tab, setTab] = useState('clientes');
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [adminModalOpen, setAdminModalOpen] = useState(false);
  const [adminSlug, setAdminSlug] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [stats, setStats] = useState({});
  const [initialTenant, setInitialTenant] = useState(null);

  // Solicitações (leads da landing page)
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(true);
  const [leadFilter, setLeadFilter] = useState('novo');
  const [leadFromId, setLeadFromId] = useState(null);

  // Audit log (master only)
  const [auditLog, setAuditLog] = useState([]);
  const [auditLoading, setAuditLoading] = useState(true);

  // WhatsApp Bot state
  const [botInfo, setBotInfo] = useState(null);
  const [botLoading, setBotLoading] = useState(true);
  const [qrData, setQrData] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const qrPollRef = useRef(null);

  const fetchBotInfo = useCallback(async () => {
    try {
      const res = await api.get('/whatsapp/admin/info');
      setBotInfo(res.data);
      if (res.data?.online && showQr) {
        setShowQr(false);
        setQrData(null);
        clearInterval(qrPollRef.current);
      }
    } catch { setBotInfo(null); }
    finally { setBotLoading(false); }
  }, [showQr]);

  const fetchQr = useCallback(async () => {
    setQrLoading(true);
    try {
      const res = await api.get('/whatsapp/qrcode');
      if (res.data?.base64) setQrData(res.data.base64);
    } catch {}
    finally { setQrLoading(false); }
  }, []);

  const handleConnect = async () => {
    setShowQr(true);
    await fetchQr();
    // Poll status every 4s and refresh QR every 30s
    qrPollRef.current = setInterval(async () => {
      await fetchBotInfo();
      await fetchQr();
    }, 30000);
    // Poll status faster separately
    const statusPoll = setInterval(async () => {
      try {
        const res = await api.get('/whatsapp/admin/info');
        setBotInfo(res.data);
        if (res.data?.online) {
          setShowQr(false);
          setQrData(null);
          clearInterval(qrPollRef.current);
          clearInterval(statusPoll);
        }
      } catch {}
    }, 4000);
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Desconectar o WhatsApp do bot?')) return;
    setDisconnecting(true);
    try {
      await api.post('/whatsapp/admin/disconnect');
      await fetchBotInfo();
    } catch {}
    finally { setDisconnecting(false); }
  };

  useEffect(() => {
    fetchBotInfo();
    const interval = setInterval(fetchBotInfo, 15000);
    return () => { clearInterval(interval); clearInterval(qrPollRef.current); };
  }, [fetchBotInfo]);

  const fetchTenants = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/superadmin/tenants');
      setTenants(res.data);
      // Fetch stats for each
      const statsMap = {};
      await Promise.all(res.data.map(async t => {
        try {
          const s = await api.get(`/superadmin/tenants/${t.slug}/stats`);
          statsMap[t.slug] = s.data;
        } catch { statsMap[t.slug] = null; }
      }));
      setStats(statsMap);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTenants(); }, [fetchTenants]);

  const fetchAuditLog = useCallback(async () => {
    setAuditLoading(true);
    try {
      const res = await api.get('/superadmin/audit-log');
      setAuditLog(res.data);
    } catch { setAuditLog([]); }
    finally { setAuditLoading(false); }
  }, []);

  useEffect(() => {
    if (admin?.isMaster) fetchAuditLog();
  }, [admin?.isMaster, fetchAuditLog]);

  const fetchLeads = useCallback(async () => {
    setLeadsLoading(true);
    try { setLeads((await api.get('/superadmin/leads')).data); }
    catch { setLeads([]); }
    finally { setLeadsLoading(false); }
  }, []);
  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const setLeadStatus = async (l, status) => {
    await api.patch(`/superadmin/leads/${l.id}`, { status });
    fetchLeads();
  };
  const removeLead = async (l) => {
    if (!window.confirm(`Remover o pedido de ${l.nome}?`)) return;
    await api.delete(`/superadmin/leads/${l.id}`);
    fetchLeads();
  };
  const createFromLead = (l) => {
    const slug = (l.empresa || l.nome).normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 30);
    setEditingTenant(null);
    setInitialTenant({ slug, nomeEmpresa: l.empresa || l.nome, nomeDoSistema: l.empresa || l.nome });
    setLeadFromId(l.id);
    setModalOpen(true);
  };
  const handleTenantSaved = async () => {
    if (leadFromId) {
      try { await api.patch(`/superadmin/leads/${leadFromId}`, { status: 'cliente_criado' }); } catch { /* ignora */ }
      setLeadFromId(null); fetchLeads();
    }
    fetchTenants();
  };

  const handleLogout = () => { logout(); navigate('/superadmin/login'); };
  const openCreate = () => { setEditingTenant(null); setInitialTenant(null); setLeadFromId(null); setModalOpen(true); };
  const openEdit = (t) => { setEditingTenant(t); setModalOpen(true); };
  const openAdminEdit = (t) => { setAdminSlug(t.slug); setAdminModalOpen(true); };

  const toggleStatus = async (t) => {
    const novo = t.status === 'ativo' ? 'suspenso' : 'ativo';
    await api.put(`/superadmin/tenants/${t.slug}`, { status: novo });
    fetchTenants();
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Excluir cliente "${t.nomeEmpresa}" e TODOS os dados? Essa ação é irreversível.`)) return;
    await api.delete(`/superadmin/tenants/${t.slug}`);
    fetchTenants();
  };

  const totalAtivos = tenants.filter(t => t.status === 'ativo').length;
  const totalSuspensos = tenants.filter(t => t.status === 'suspenso').length;

  const menu = [
    { key: 'clientes', label: 'Clientes', icon: Building2, testId: 'sa-menu-clientes' },
    { key: 'solicitacoes', label: 'Solicitações', icon: Inbox, testId: 'sa-menu-solicitacoes' },
    { key: 'whatsapp', label: 'WhatsApp Bot', icon: MessageCircle, testId: 'sa-menu-whatsapp' },
    ...(admin?.isMaster ? [{ key: 'auditoria', label: 'Auditoria', icon: ShieldCheck, testId: 'sa-menu-auditoria' }] : []),
  ].map(i => ({ ...i, onClick: () => setTab(i.key), active: tab === i.key }));

  const head = TITLES[tab] || TITLES.clientes;

  return (
    <div className="flex min-h-screen bg-[#EAEFF5] dark:bg-[#000000] text-slate-800 dark:text-slate-100 antialiased font-['Plus_Jakarta_Sans',sans-serif] transition-colors duration-200"
      data-testid="superadmin-dashboard-page">
      <AppSidebar
        logo=""
        fallbackLogo={<GastifyIcon />}
        title="Gastify"
        subtitle="Painel Master"
        items={menu}
        user={{
          name: admin?.nomeCompleto || admin?.username,
          username: admin?.username,
          fotoPerfil: admin?.fotoPerfil,
        }}
        profileLabel={admin?.isMaster ? 'Master' : 'Meu perfil'}
        onOpenProfile={() => setProfileOpen(true)}
        onLogout={handleLogout}
        mobileTitle="Gastify · Painel Master"
      />

      <main className="flex-1 lg:ml-[272px] pt-16 lg:pt-4 p-4 sm:p-6 lg:p-6 lg:pl-2 min-h-screen overflow-auto">
        <div className="space-y-6 page-enter pb-10">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-[26px] font-bold tracking-tight text-slate-800"
                style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                {head.title}
              </h1>
              <p className="text-slate-500 text-sm mt-1">{head.sub}</p>
            </div>
            {tab === 'clientes' && (
              <button onClick={openCreate} data-testid="sa-create-tenant-btn"
                className="flex items-center justify-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold px-3.5 py-2.5 rounded-2xl shadow-md transition-all active:scale-[0.98]">
                <Plus size={14} /> Novo Cliente
              </button>
            )}
          </div>

          {/* ───────── CLIENTES ───────── */}
          {tab === 'clientes' && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {[
                  { label: 'Total de Clientes', value: tenants.length, icon: Building2, color: '#0147a0' },
                  { label: 'Ativos', value: totalAtivos, icon: Activity, color: '#339933' },
                  { label: 'Suspensos', value: totalSuspensos, icon: Pause, color: '#d72d21' },
                ].map(c => (
                  <div key={c.label} className="fin-card p-5" data-testid={`sa-stat-${c.label.toLowerCase().replace(/\s+/g, '-')}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">{c.label}</p>
                        <p className="text-3xl font-bold" style={{ color: c.color, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{c.value}</p>
                      </div>
                      <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ background: `${c.color}15` }}>
                        <c.icon size={18} style={{ color: c.color }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="fin-card" data-testid="sa-tenants-table">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-100">
                        {['Cliente', 'Slug', 'Status', 'Lançamentos', 'Criado em', 'Último acesso', 'Ações'].map(h => (
                          <th key={h} className="text-left px-5 py-3 text-xs font-bold uppercase tracking-widest text-slate-400">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {loading ? (
                        Array(3).fill(0).map((_, i) => (
                          <tr key={i}>{Array(7).fill(0).map((_, j) => (
                            <td key={j} className="px-5 py-4"><div className="h-3 bg-slate-100 rounded animate-pulse" /></td>
                          ))}</tr>
                        ))
                      ) : tenants.length === 0 ? (
                        <tr><td colSpan={7} className="px-5 py-12 text-center text-slate-400">
                          Nenhum cliente cadastrado. Clique em "Novo Cliente" para começar.
                        </td></tr>
                      ) : tenants.map(t => (
                        <tr key={t.slug} className="hover:bg-slate-50 transition-colors" data-testid={`sa-tenant-row-${t.slug}`}>
                          <td className="px-5 py-4">
                            <div className="flex items-start gap-3 min-w-[180px]">
                              {t.logo ? (
                                <img src={t.logo} alt="" className="w-9 h-9 rounded-xl object-cover border border-slate-200 flex-shrink-0 mt-0.5" />
                              ) : (
                                <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0 mt-0.5"
                                  style={{ background: '#6366F1' }}>
                                  {(t.nomeEmpresa || t.slug)[0]?.toUpperCase()}
                                </div>
                              )}
                              <div className="min-w-0 flex-1">
                                <p className="font-semibold text-slate-700 leading-tight break-words" style={{ wordBreak: 'break-word' }}>{t.nomeEmpresa}</p>
                                <p className="text-xs text-slate-400 mt-0.5 leading-tight break-words" style={{ wordBreak: 'break-word' }}>{t.nomeDoSistema}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-4">
                            <code className="text-xs bg-slate-100 px-2 py-1 rounded-lg whitespace-nowrap">{t.slug}</code>
                          </td>
                          <td className="px-5 py-4"><StatusBadge type="status" value={t.status} /></td>
                          <td className="px-5 py-4 text-slate-700 font-semibold">{stats[t.slug]?.totalLancamentos ?? '—'}</td>
                          <td className="px-5 py-4 text-slate-500 text-xs">{formatDate(t.dataCriacao)}</td>
                          <td className="px-5 py-4 text-slate-500 text-xs">{formatDate(t.ultimoAcesso)}</td>
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-1">
                              <a href={`/${t.slug}/login`} target="_blank" rel="noopener noreferrer"
                                title="Abrir tenant"
                                className="p-2 rounded-xl text-slate-400 hover:text-[#0147a0] hover:bg-blue-50 transition-colors"
                                data-testid={`sa-open-tenant-${t.slug}`}>
                                <ExternalLink size={14} />
                              </a>
                              <button onClick={() => openEdit(t)} title="Editar tenant"
                                className="p-2 rounded-xl text-slate-400 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                                data-testid={`sa-edit-tenant-${t.slug}`}>
                                <Edit2 size={14} />
                              </button>
                              <button onClick={() => openAdminEdit(t)} title="Editar admin do tenant"
                                className="p-2 rounded-xl text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                                data-testid={`sa-edit-admin-${t.slug}`}>
                                <UserCog size={14} />
                              </button>
                              <button onClick={() => toggleStatus(t)} title={t.status === 'ativo' ? 'Suspender' : 'Reativar'}
                                className="p-2 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                                data-testid={`sa-toggle-tenant-${t.slug}`}>
                                {t.status === 'ativo' ? <Pause size={14} /> : <Play size={14} />}
                              </button>
                              <button onClick={() => handleDelete(t)} title="Excluir"
                                className="p-2 rounded-xl text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                data-testid={`sa-delete-tenant-${t.slug}`}>
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* ───────── WHATSAPP BOT ───────── */}
          {tab === 'whatsapp' && (
            <div className="fin-card p-5" data-testid="sa-whatsapp-panel">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <MessageCircle size={18} className="text-green-500" />
                  <h2 className="font-bold text-slate-700 text-sm">Status da conexão</h2>
                </div>
                <button onClick={fetchBotInfo} className="p-1.5 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors" title="Atualizar">
                  <RefreshCw size={14} className={botLoading ? 'animate-spin' : ''} />
                </button>
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${botInfo?.online ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
                    {botInfo?.online ? <Wifi size={18} /> : <WifiOff size={18} />}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-700">
                      {botLoading ? 'Verificando...' : botInfo?.online ? 'Bot conectado' : 'Bot desconectado'}
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {botInfo?.online && botInfo?.phone ? `Número: +${botInfo.phone}` : botInfo?.online ? 'Número não identificado' : 'Nenhum WhatsApp vinculado ao bot'}
                    </p>
                  </div>
                  <StatusBadge type="status" value={botLoading ? '...' : botInfo?.online ? 'ativo' : 'suspenso'} muted={!botInfo?.online} />
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  {botInfo?.online ? (
                    <button onClick={handleDisconnect} disabled={disconnecting}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-2xl text-xs font-semibold text-rose-600 bg-rose-50 hover:bg-rose-100 transition-colors">
                      <Unlink2 size={13} /> {disconnecting ? 'Desconectando...' : 'Desconectar'}
                    </button>
                  ) : (
                    <button onClick={handleConnect} disabled={showQr}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-2xl text-xs font-semibold text-white bg-green-600 hover:bg-green-700 transition-colors">
                      <QrCode size={13} /> {showQr ? 'Aguardando scan...' : 'Conectar Bot'}
                    </button>
                  )}
                  {botInfo?.online && (
                    <button onClick={handleConnect}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-2xl text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors">
                      <RefreshCw size={13} /> Reconectar
                    </button>
                  )}
                </div>
              </div>

              {showQr && (
                <div className="mt-4 flex flex-col items-center gap-3 p-5 rounded-2xl bg-slate-50 border border-slate-200">
                  <p className="text-xs font-semibold text-slate-600">Escaneie o QR Code com o WhatsApp do bot</p>
                  {qrLoading && !qrData ? (
                    <div className="w-48 h-48 bg-slate-200 rounded-2xl animate-pulse flex items-center justify-center">
                      <QrCode size={32} className="text-slate-400" />
                    </div>
                  ) : qrData ? (
                    <img src={qrData} alt="QR Code WhatsApp" className="w-48 h-48 rounded-2xl border border-slate-200 bg-white p-1" />
                  ) : (
                    <p className="text-xs text-slate-400">Não foi possível carregar o QR Code.</p>
                  )}
                  <p className="text-[11px] text-slate-400 text-center">O QR Code atualiza automaticamente a cada 30s.<br />Após escanear, aguarde a confirmação acima.</p>
                  <button onClick={() => { setShowQr(false); setQrData(null); clearInterval(qrPollRef.current); }}
                    className="text-xs text-slate-500 hover:text-slate-700 underline">Cancelar</button>
                </div>
              )}
            </div>
          )}

          {/* ───────── SOLICITAÇÕES ───────── */}
          {tab === 'solicitacoes' && (
            <div className="space-y-4" data-testid="sa-leads-panel">
              <div className="flex items-center gap-2 flex-wrap">
                {[['novo', 'Novos'], ['contatado', 'Contatados'], ['cliente_criado', 'Clientes criados'], ['descartado', 'Descartados'], ['todos', 'Todos']].map(([k, lbl]) => (
                  <button key={k} onClick={() => setLeadFilter(k)}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-colors ${leadFilter === k ? 'bg-slate-900 text-white' : 'bg-white text-slate-500 hover:bg-slate-100'}`}>
                    {lbl} ({k === 'todos' ? leads.length : leads.filter(x => x.status === k).length})
                  </button>
                ))}
                <button onClick={fetchLeads} className="ml-auto p-1.5 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-white" title="Atualizar">
                  <RefreshCw size={14} className={leadsLoading ? 'animate-spin' : ''} />
                </button>
              </div>
              {(() => {
                const shown = leads.filter(l => leadFilter === 'todos' || l.status === leadFilter);
                if (leadsLoading) return <div className="fin-card p-6"><div className="h-4 bg-slate-100 rounded animate-pulse" /></div>;
                if (shown.length === 0) return <div className="fin-card p-10 text-center text-sm text-slate-400">Nenhuma solicitação aqui.</div>;
                return shown.map(l => (
                  <div key={l.id} className="fin-card p-4 sm:p-5 space-y-3" data-testid={`sa-lead-${l.id}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <p className="font-bold text-slate-800">{l.nome} <span className="ml-1 text-[10px] font-extrabold uppercase px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600">{l.tipo}</span></p>
                        {l.empresa && <p className="text-xs text-slate-500">{l.empresa}</p>}
                      </div>
                      <span className="text-xs text-slate-400">{formatDate(l.criadoEm)}</span>
                    </div>
                    <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-600">
                      <a className="inline-flex items-center gap-1.5 hover:text-emerald-600" href={`https://wa.me/${l.whatsapp.length <= 11 ? '55' : ''}${l.whatsapp}`} target="_blank" rel="noreferrer"><Phone size={13} />{l.whatsapp}</a>
                      <a className="inline-flex items-center gap-1.5 hover:text-indigo-600" href={`mailto:${l.email}`}><Mail size={13} />{l.email}</a>
                    </div>
                    {l.mensagem && <p className="text-sm text-slate-500 bg-slate-50 rounded-xl px-3 py-2">{l.mensagem}</p>}
                    <div className="flex items-center gap-2 flex-wrap pt-1">
                      <span className="text-xs font-bold text-slate-500">{{ novo: 'Novo', contatado: 'Contatado', cliente_criado: 'Cliente criado', descartado: 'Descartado' }[l.status]}</span>
                      <div className="ml-auto flex items-center gap-2 flex-wrap">
                        {l.status === 'novo' && <button onClick={() => setLeadStatus(l, 'contatado')} className="text-xs font-semibold px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700">Marcar contatado</button>}
                        {l.status !== 'cliente_criado' && <button onClick={() => createFromLead(l)} className="text-xs font-semibold px-3 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="sa-lead-criar">Criar cliente</button>}
                        {l.status !== 'descartado' && l.status !== 'cliente_criado' && <button onClick={() => setLeadStatus(l, 'descartado')} className="text-xs font-semibold px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-500">Descartar</button>}
                        <button onClick={() => removeLead(l)} className="p-1.5 rounded-xl text-red-400 hover:bg-red-50" aria-label="Remover"><Trash2 size={14} /></button>
                      </div>
                    </div>
                  </div>
                ));
              })()}
            </div>
          )}

          {/* ───────── AUDITORIA (master) ───────── */}
          {tab === 'auditoria' && admin?.isMaster && (
            <div className="fin-card p-5" data-testid="sa-audit-log-panel">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <ShieldCheck size={18} className="text-indigo-500" />
                  <h2 className="font-bold text-slate-700 text-sm">Últimas ações</h2>
                </div>
                <button onClick={fetchAuditLog} className="p-1.5 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors" title="Atualizar">
                  <RefreshCw size={14} className={auditLoading ? 'animate-spin' : ''} />
                </button>
              </div>
              {auditLoading ? (
                <div className="space-y-2">
                  {Array(3).fill(0).map((_, i) => (
                    <div key={i} className="h-4 bg-slate-100 rounded animate-pulse" />
                  ))}
                </div>
              ) : auditLog.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">Nenhuma ação registrada ainda.</p>
              ) : (
                <ul className="divide-y divide-slate-50 max-h-[60vh] overflow-y-auto" data-testid="sa-audit-log-list">
                  {auditLog.map(entry => (
                    <li key={entry.id} className="py-2.5 flex items-start justify-between gap-4">
                      <span className="text-sm text-slate-700">{entry.texto}</span>
                      <span className="text-xs text-slate-400 whitespace-nowrap flex-shrink-0">{formatDate(entry.timestamp)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </main>

      <TenantModal isOpen={modalOpen} onClose={() => setModalOpen(false)}
        tenant={editingTenant} initial={initialTenant} onSuccess={handleTenantSaved} />
      <TenantAdminModal isOpen={adminModalOpen} onClose={() => setAdminModalOpen(false)}
        slug={adminSlug} onSuccess={fetchTenants} />
      <ProfileModal isOpen={profileOpen} onClose={() => setProfileOpen(false)}
        kind="superadmin" currentUser={admin} onSuccess={refreshAdmin} />
    </div>
  );
}
